// Receive stock (UC-PURCHASING-005, UC-PRICING-007; W9, CAT-66): a delivery no purchase order covers, taken into the acting branch —
// /stock/receive from Stock, /purchasing/receipts/new from Purchasing › Goods receipts. The prototype draws no receipt without an
// order (stock.html's Receive delivery opens Purchasing; purchasing.html's goods receipt is against PO-MEN-000226), so the page is
// designed from the use case in the prototype's vocabulary: the Delivery panel — the supplier from the Active ones with the branch's
// account, the received date (today, an earlier day allowed, never a later one), the delivery note with the warning of one already
// received from the supplier (A14), the reason there is no order and notes — and the Lines panel: a scanned or typed code adds a
// line; each line takes its quantity and ex-VAT unit cost and shows its total, the proposal pricing makes from the cost beside the
// price the branch charges today (BR-PURCHASING-017), the price kept — the proposal unless changed, with Use to go back to it and a
// reason for any other — and what the kept price will change (BR-PRICING-016); a serialised line has one row per unit, each with an
// optional supplier serial or barcode and its own price when it is priced apart (CAT-34). A kept price below its floor (the discount
// thresholds, BR-PRICING-017) and a line that sets the branch's price (BR-PRICING-016; DVN's answer of 24 Oct 2026) wear the lock mark,
// and posting asks for one Control PIN (`PriceOverride`) naming those prices; the
// same receipt is sent again under the same key with the token. The refusals sit under their fields, lines and units, a fresh
// proposal replacing the one shown (E5). Once posted the page shows the receipt as posted, with its GRN number and serials.
import {
  LINE_QUANTITY_MAX, PURCHASING_LIMITS, UNIT_COST_MAX_CENTS, cents,
  type GoodsReceiptInput, type GoodsReceiptLineInput, type GoodsReceiptUnitInput, type GoodsReceiptWriteResult, type ProductSummary, type SuggestResult, type SupplierListItem,
} from "@visionx/contracts";
import { sumCents, timesQuantity } from "@visionx/money";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { products } from "../api/endpoints.catalogue";
import { pricing } from "../api/endpoints.pricing";
import { goodsReceipts } from "../api/endpoints.purchasing";
import { carriedOut, describeError, isApiError, isNetworkError, type ApiError } from "../api/problem";
import { formatCount, formatMoney, localDate } from "../lib/format";
import { centsFromText, textFromCents } from "../lib/money-input";
import { can, useSessionView } from "../session/store";
import { fieldScanCollector } from "../shell/key-burst";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, Chip, Field, LockMark, Panel, TextArea, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { SupplierPicker } from "./CatalogueShared";
import { NoAccessPanel, NoActiveBranch, PageHead } from "./common";
import { useDebounce, useLeaveGuard } from "./PatientsShared";
import { possessive } from "./PricingShared";
import { ReceiptBody, ReceiptLink, ReceiptWarnings, belowFloor, dayWords, floorOf, suggestionBasis, useDiscountThresholds, type Thresholds } from "./ReceiptsShared";
import { CameraScan, readsAllBranches, shortName, useActingBranch, useStockWrites } from "./StockShared";

/** A supplier's serial may not have the shape of a number Vision X generates (BR-PURCHASING-016, `reserved_format`). */
const GENERATED = /^\d{4}-\d{2}-\d{2}-\d{3,}$/;
const UNITS_MAX = PURCHASING_LIMITS.receiptSerialisedUnitsMax;

interface UnitDraft { key: string; serial: string; keptText: string | null; reason: string }
interface LineDraft {
  key: string;
  product: ProductSummary;
  qtyText: string;
  costText: string;
  /** The price kept as typed; null follows the price offered (the proposal, else the current price). */
  keptText: string | null;
  reason: string;
  /** A serialised line's units, one per unit received. */
  units: UnitDraft[];
  /** The proposal the server made when it refused the receipt (E5), for the cost it was made at. */
  fresh: { costCents: number; proposedCents: number | null; currentCents: number | null } | null;
}

let seq = 0;
const nextKey = (p: string) => `${p}${++seq}`;
const newUnit = (): UnitDraft => ({ key: nextKey("u"), serial: "", keptText: null, reason: "" });

/** A whole number of units, 1–9 999 (W4); null for anything else. */
const qtyOf = (t: string): number | null => { const s = t.trim(); return /^\d{1,4}$/.test(s) && Number(s) >= 1 && Number(s) <= LINE_QUANTITY_MAX ? Number(s) : null; };
/** An amount typed in rand, R 0.00–R 999 999.99; null for anything else. */
const amountOf = (t: string): number | null => { const c = centsFromText(t); return c !== null && c <= UNIT_COST_MAX_CENTS ? c : null; };
/** A kept price as typed: blank follows the price offered. */
const typed = (t: string | null): string | null => (t === null || t.trim() === "" ? null : t);

/** A serialised line's unit rows follow its quantity, keeping what each unit already holds; never more than a receipt may hold. */
function withUnits(l: LineDraft, qtyText: string): LineDraft {
  if (!l.product.trackSerials) return { ...l, qtyText };
  const q = qtyOf(qtyText);
  if (q === null) return { ...l, qtyText };
  const n = Math.min(q, UNITS_MAX);
  const units = l.units.slice(0, n);
  while (units.length < n) units.push(newUnit());
  return { ...l, qtyText, units };
}

const receivable = (p: ProductSummary): boolean => p.status === "Active" && p.stocked && !p.isComposite;

interface UnitPlan { kept: number | null; own: boolean; invalid: boolean; needsReason: boolean; below: boolean; serialIssue: string | null }
interface LinePlan {
  qty: number | null; cost: number | null; total: number | null;
  current: number | null; sug: SuggestResult | undefined; pending: boolean;
  proposing: boolean; proposed: number | null; offered: number | null;
  kept: number | null; keptInvalid: boolean; needsPrice: boolean; needsReason: boolean; below: boolean;
  /** A line without serials kept at a price other than the one in force sets the branch's price, which needs the PIN (BR-PRICING-016). */
  setsBranch: boolean;
  units: UnitPlan[];
}

/** /stock/receive and /purchasing/receipts/new */
export function ReceiveStock() {
  const s = useSessionView();
  const qc = useQueryClient();
  const loc = useLocation();
  const fromStock = loc.pathname.startsWith("/stock");
  const back = fromStock ? <Link className="btn" to="/stock">Back to stock</Link> : <Link className="btn" to="/purchasing/receipts">Goods receipts</Link>;
  const [posted, setPosted] = useState<GoodsReceiptWriteResult | null>(null);
  const [round, setRound] = useState(0);
  if (!can(s, "purchasing.po.write")) {
    return <section className="screen" id="s-receive-stock"><div className="content"><PageHead title="Receive stock" actions={back} /><NoAccessPanel page="Goods receipts" permission="purchasing.po.write" /></div></section>;
  }
  // A receipt is posted at the acting branch (E8): with none, nothing can be priced or posted.
  if (!s?.activeBranchId) {
    return <section className="screen" id="s-receive-stock"><div className="content"><PageHead title="Receive stock" actions={back} /><NoActiveBranch what="the branch whose stock a delivery enters" /></div></section>;
  }
  if (posted) {
    return <PostedReceipt r={posted} back={back} onAnother={() => { setPosted(null); setRound((n) => n + 1); }} />;
  }
  return (
    <ReceiveForm key={round} back={back} onPosted={(r) => {
      for (const k of ["stock", "serials", "goods-receipts", "product", "products", "pricing"]) void qc.invalidateQueries({ queryKey: [k] });
      setPosted(r);
    }} />
  );
}

function PostedReceipt({ r, back, onAnother }: { r: GoodsReceiptWriteResult; back: ReactNode; onAnother: () => void }) {
  const serials = r.lines.reduce((n, l) => n + l.units.length, 0);
  // Posting is pressed at the foot of the lines: the posted receipt is read from its head.
  const top = useRef<HTMLElement>(null);
  useEffect(() => { top.current?.scrollIntoView?.({ block: "start" }); }, []);
  return (
    <section className="screen" id="s-receive-stock" ref={top}>
      <div className="content">
        <PageHead
          title={<span className="row c gap-8">Receipt <span className="mono">{r.number}</span><Chip tone="ok" dot>Posted</Chip></span>}
          sub={`${r.branch.name} · ${r.supplier.name} · no purchase order · received ${dayWords(r.receivedDate)}${r.receivedBy ? ` by ${shortName(r.receivedBy.displayName)}` : ""}`}
          actions={<>{back}<Link className="btn" to={`/purchasing/receipts/${r.id}`}>Open receipt</Link><button className="btn primary" type="button" onClick={onAnother}><Icon name="plus" />Receive another delivery</button></>}
        />
        <Alert tone="ok" role="status">
          <b>{r.number} posted.</b> {r.lineCount} line{r.lineCount === 1 ? "" : "s"}, {r.unitCount} unit{r.unitCount === 1 ? "" : "s"}, {formatMoney(r.totalExVatCents)} ex VAT into {r.branch.name}&#39;s stock{serials > 0 ? `, with ${serials} serial${serials === 1 ? "" : "s"}` : ""}. Labels are printed from Sprint 13.
        </Alert>
        <ReceiptWarnings warnings={r.warnings} lines={r.lines} />
        <ReceiptBody v={r} />
      </div>
    </section>
  );
}

function ReceiveForm({ back, onPosted }: { back: ReactNode; onPosted: (r: GoodsReceiptWriteResult) => void }) {
  const s = useSessionView();
  const withStepUp = useStepUp();
  const acting = useActingBranch();
  const { online, title: blockTitle } = useStockWrites();
  const thresholds = useDiscountThresholds();
  const today = localDate(new Date());
  const [supplier, setSupplier] = useState<SupplierListItem | null>(null);
  const [receivedDate, setReceivedDate] = useState(today);
  const [note, setNote] = useState("");
  const [noteChecked, setNoteChecked] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([]);
  // The lines as last set, for a scan answered after a render or two: a code taken from a line's quantity puts that quantity back
  // before the code is looked up, and the line it adds a unit to is read as it is then (A19; TC-PURCHASING-125).
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const [scan, setScan] = useState("");
  const [scanMsg, setScanMsg] = useState<ReactNode>(null);
  const [choices, setChoices] = useState<ProductSummary[] | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [cancelled, setCancelled] = useState(false);
  // A post whose answer never came (the connection dropped, or it is still being written): its key, and the receipt as it was
  // sent. Until an answer says what became of it, every post goes under that key, even after an edit — so the delivery is never
  // taken in twice — and the screen says what posting again will do (the review of 18 Oct 2026, D1-11; TC-PURCHASING-114).
  const [lost, setLost] = useState<{ key: string; bodyText: string } | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const branchId = acting.id ?? "";

  useEffect(() => {
    if (!focusKey) return;
    const el = document.getElementById(`qty-${focusKey}`) as HTMLInputElement | null;
    el?.focus();
    el?.select();
    setFocusKey(null);
  }, [focusKey]);

  // --- the figures pricing gives each line ----------------------------------------------------------------------------------
  const productIds = useMemo(() => [...new Set(lines.map((l) => l.product.id))], [lines]);
  const currents = useQueries({
    queries: productIds.map((pid) => ({
      queryKey: ["receipt", "current", branchId, pid, today],
      queryFn: () => pricing.resolve({ branchId, documentDate: today, lines: [{ productId: pid, quantity: 1 }] }),
      enabled: branchId !== "", staleTime: 60_000,
    })),
  });
  const currentOf = (pid: string): { known: boolean; cents: number | null } => {
    const r = currents[productIds.indexOf(pid)];
    const line = r?.data?.data.lines[0];
    if (!r || r.isPending) return { known: false, cents: null };
    return { known: true, cents: line && line.priceSource !== "None" ? line.unitPriceCents : null };
  };
  // The proposal follows the cost as it is typed, a moment after the typing stops (or at once when the field is left).
  const costsNow = JSON.stringify(lines.map((l) => [l.key, amountOf(l.costText)]));
  const [costsSettled, settleCosts] = useDebounce(costsNow, 300);
  const settled = useMemo(() => new Map<string, number | null>(JSON.parse(costsSettled) as [string, number | null][]), [costsSettled]);
  const asked = lines.map((l) => ({ key: l.key, productId: l.product.id, cost: settled.get(l.key) ?? null }));
  const suggestions = useQueries({
    queries: asked.map((a) => ({
      queryKey: ["receipt", "suggest", branchId, a.productId, a.cost],
      queryFn: () => pricing.suggest({ branchId, productId: a.productId, netCostCents: a.cost! }),
      enabled: branchId !== "" && a.cost !== null, staleTime: 60_000,
    })),
  });

  const plans: LinePlan[] = lines.map((l, i) => {
    const qty = qtyOf(l.qtyText);
    const cost = amountOf(l.costText);
    const total = qty !== null && cost !== null ? timesQuantity(cents(cost), qty) : null;
    const cur = currentOf(l.product.id);
    const q = suggestions[i];
    const sug = cost !== null && asked[i]?.cost === cost ? q?.data?.data : undefined;
    const pending = cost !== null && (asked[i]?.cost !== cost || !!q?.isPending || !cur.known);
    const fresh = l.fresh && l.fresh.costCents === cost ? l.fresh : null;
    const current = fresh?.currentCents ?? cur.cents;
    const proposing = sug ? sug.proposePriceAtReceipt : true;
    const proposed = fresh ? fresh.proposedCents : sug?.proposal?.suggestedIncVatCents ?? null;
    const offered = proposing && (sug || fresh) ? proposed ?? current : null;
    const keptTyped = typed(l.keptText);
    const keptInvalid = keptTyped !== null && amountOf(keptTyped) === null;
    const kept = keptTyped !== null ? amountOf(keptTyped) : offered;
    const ready = !pending && cost !== null;
    const needsPrice = ready && proposing && kept === null && !keptInvalid;
    const allSerials = lines.flatMap((x) => x.units.map((u) => u.serial.trim()).filter(Boolean));
    // With proposals off nothing is offered, so a price needs a reason only below its floor (A10 step 2): asked for here, before
    // posting, from the floor the screen works out, as the server's 422 would ask after it (the review of 18 Oct 2026; TC-PURCHASING-124).
    const units: UnitPlan[] = l.units.map((u) => {
      const ut = typed(u.keptText);
      const invalid = ut !== null && amountOf(ut) === null;
      const uKept = ut !== null ? amountOf(ut) : kept;
      const own = ut !== null && !invalid && uKept !== kept;
      const sr = u.serial.trim();
      const serialIssue = sr === "" ? null : GENERATED.test(sr) ? "This has the shape of a serial Vision X generates — type the supplier's own" : allSerials.filter((x) => x === sr).length > 1 ? "Repeated on this receipt" : null;
      const uBelow = ready && belowFloor(current, uKept, thresholds);
      return { kept: uKept, own, invalid, needsReason: ready && own && (proposing ? offered !== null && uKept !== offered : uBelow), below: uBelow, serialIssue };
    });
    const below = ready && !l.product.trackSerials && belowFloor(current, kept, thresholds);
    // DVN's answer of 24 Oct 2026 to Sprint 4's question 3: a line that sets the branch's price needs the receipt's PIN, raised or lowered.
    const setsBranch = ready && !l.product.trackSerials && kept !== null && current !== null && kept !== current;
    // The line's price stands for each unit not priced apart, so the line's reason is the one asked when such a unit is below.
    const lineBelow = l.product.trackSerials ? units.some((u) => !u.own && u.below) : below;
    const needsReason = ready && kept !== null && (proposing ? offered !== null && kept !== offered : lineBelow);
    return { qty, cost, total, current, sug, pending, proposing, proposed, offered, kept, keptInvalid, needsPrice, needsReason, below, setsBranch, units };
  });

  // --- the delivery note already received (A14) --------------------------------------------------------------------------------
  const allBranches = readsAllBranches(s?.user.role);
  const dup = useQuery({
    queryKey: ["goods-receipts", "note", supplier?.id ?? "", noteChecked],
    queryFn: () => goodsReceipts.list({ supplierId: supplier!.id, deliveryNoteNumber: noteChecked, branchId: allBranches ? "all" : undefined }, { limit: 1 }),
    enabled: supplier !== null && noteChecked !== "", retry: false,
  });
  const dupRow = noteChecked !== "" && noteChecked === note.trim() ? dup.data?.data.items[0] : undefined;

  // --- adding a line -------------------------------------------------------------------------------------------------------------
  const addLine = (p: ProductSummary, separate = false) => {
    setChoices(null);
    const now = linesRef.current;
    const last = [...now].reverse().find((l) => l.product.id === p.id);
    if (last && !separate) {
      const q = qtyOf(last.qtyText) ?? 0;
      setLines((ls) => ls.map((l) => (l.key === last.key ? withUnits(l, String(Math.min(q + 1, LINE_QUANTITY_MAX))) : l)));
      setScanMsg(<>{p.displayName} is on line {now.indexOf(last) + 1}: its quantity is now {q + 1}. <button className="linkbtn" type="button" onClick={() => {
        setLines((ls) => ls.map((l) => (l.key === last.key ? withUnits(l, String(Math.max(q, 1))) : l)));
        addLine(p, true);
      }}>Put it on a line of its own</button> — the same product at another cost is its own line.</>);
      return;
    }
    const line: LineDraft = { key: nextKey("l"), product: p, qtyText: "1", costText: "", keptText: null, reason: "", units: p.trackSerials ? [newUnit()] : [], fresh: null };
    setLines((ls) => [...ls, line]);
    setScanMsg(null);
    setFocusKey(line.key);
  };
  const resolve = async (code: string) => {
    const c = code.trim();
    if (!c) return;
    setScanMsg(null);
    setChoices(null);
    try {
      const looked = await products.lookup(c).catch((e: unknown) => { if (isApiError(e) && e.code === "NOT_FOUND") return null; throw e; });
      let found: ProductSummary[] = looked ? [...new Map(looked.data.items.map((x) => [x.product.id, x.product])).values()] : [];
      if (found.length === 0) found = (await products.list({ q: c }, { limit: 10 })).data.items;
      const ok = found.filter(receivable);
      if (ok.length === 1) addLine(ok[0]!);
      else if (ok.length > 1) setChoices(ok);
      else if (found.length > 0) {
        const p = found[0]!;
        setScanMsg(p.status !== "Active" ? <><span className="mono">{p.sku}</span> {p.displayName} is inactive: it is not received.</> : <><span className="mono">{p.sku}</span> {p.displayName} holds no stock — a service, a made-to-order lens or a composite is not received.</>);
      } else setScanMsg(<>Nothing in the catalogue matches <span className="mono">{c}</span>.</>);
      setScan("");
    } catch (err) { setScanMsg(describeError(err)); }
    // A new line takes the focus to its quantity once it renders (the effect above); otherwise the scan box keeps it.
    finally { scanRef.current?.focus(); }
  };

  const change = (key: string, f: (l: LineDraft) => LineDraft) => { setLines((ls) => ls.map((l) => (l.key === key ? f(l) : l))); setCancelled(false); };
  const changeUnit = (key: string, uKey: string, f: (u: UnitDraft) => UnitDraft) => change(key, (l) => ({ ...l, units: l.units.map((u) => (u.key === uKey ? f(u) : u)) }));
  const toScan = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) { e.preventDefault(); settleCosts(); scanRef.current?.focus(); } };
  /**
   * A scanner's code that landed in a line's quantity or unit cost — the focus is on a new line's quantity — is a scan (A19): the
   * box goes back to what it held before the code began, and the code is looked up as if scanned into Add product, adding a unit or
   * a line (the hands-on test of 21 Oct 2026; TC-PURCHASING-125).
   */
  const scanFromLine = (lineKey: string, field: "qty" | "cost", before: string, code: string) => {
    const restore = (l: LineDraft): LineDraft => (l.key !== lineKey ? l : field === "qty" ? withUnits(l, before) : { ...l, costText: before });
    linesRef.current = linesRef.current.map(restore);
    setLines((ls) => ls.map(restore));
    setScan(code);
    void resolve(code);
  };

  // --- the request ---------------------------------------------------------------------------------------------------------------
  const body: GoodsReceiptInput = {
    supplierId: supplier?.id ?? "",
    ...(receivedDate && receivedDate !== today ? { receivedDate: receivedDate as NonNullable<GoodsReceiptInput["receivedDate"]> } : {}),
    ...(note.trim() ? { deliveryNoteNumber: note.trim() } : {}),
    reason: reason.trim(),
    ...(notes.trim() ? { notes: notes.trim() } : {}),
    lines: lines.map((l, i): GoodsReceiptLineInput => {
      const p = plans[i]!;
      const units: GoodsReceiptUnitInput[] = l.units.map((u, j) => {
        const up = p.units[j]!;
        return {
          ...(u.serial.trim() ? { supplierSerial: u.serial.trim() } : {}),
          ...(up.own && up.kept !== null ? { keptPriceCents: up.kept } : {}),
          ...((up.needsReason || l.fresh !== null) && up.own && u.reason.trim() ? { priceReason: u.reason.trim() } : {}),
        };
      });
      return {
        productId: l.product.id, quantity: p.qty ?? 0, unitCostCents: p.cost ?? 0,
        ...(p.kept !== null ? { keptPriceCents: p.kept } : {}),
        ...((p.needsReason || l.fresh !== null) && l.reason.trim() ? { priceReason: l.reason.trim() } : {}),
        ...(units.some((u) => Object.keys(u).length > 0) ? { units } : {}),
      };
    }),
  };
  const bodyText = JSON.stringify(body);
  // One key per receipt as it stands: the same receipt sent again — after a lost answer, or with the Control PIN — posts once (A12).
  const key = useMemo(() => newIdempotencyKey(), [bodyText]);
  // Typed work is guarded against a refresh or a closed tab, as the product form's is (D1-10; TC-PURCHASING-115).
  const typedWork = supplier !== null || note.trim() !== "" || reason.trim() !== "" || notes.trim() !== "" || lines.length > 0 || receivedDate !== today;
  useLeaveGuard(busy || typedWork);

  // What Post receipt waits for, in words.
  const serialised = plans.reduce((n, p, i) => n + (lines[i]!.product.trackSerials ? p.qty ?? 0 : 0), 0);
  const waiting: string[] = [];
  if (!supplier) waiting.push("a supplier");
  if (!reason.trim()) waiting.push("the reason there is no order");
  if (!receivedDate || receivedDate > today) waiting.push("a received date no later than today");
  if (lines.length === 0) waiting.push("a line");
  if (serialised > UNITS_MAX) waiting.push(`at most ${UNITS_MAX} serialised units`);
  lines.forEach((l, i) => {
    const p = plans[i]!;
    const n = i + 1;
    if (p.qty === null) waiting.push(`line ${n}'s quantity`);
    if (p.cost === null) waiting.push(`line ${n}'s unit cost`);
    else if (p.pending) waiting.push(`line ${n}'s proposal`);
    if (p.needsPrice || p.keptInvalid) waiting.push(`line ${n}'s price`);
    if (p.needsReason && !l.reason.trim()) waiting.push(`the reason for line ${n}'s price`);
    p.units.forEach((u, j) => {
      if (u.invalid) waiting.push(`unit ${j + 1}'s price on line ${n}`);
      if (u.needsReason && !l.units[j]!.reason.trim()) waiting.push(`the reason for unit ${j + 1}'s price on line ${n}`);
      if (u.serialIssue) waiting.push(`unit ${j + 1}'s serial on line ${n}`);
    });
  });
  const below = lines.flatMap((l, i) => {
    const p = plans[i]!;
    const floor = floorOf(p.current, thresholds);
    if (!l.product.trackSerials) return p.below ? [{ what: l.product.displayName, kept: p.kept!, floor: floor! }] : [];
    return p.units.flatMap((u, j) => (u.below ? [{ what: `${l.product.displayName}, unit ${j + 1}`, kept: u.kept!, floor: floor! }] : []));
  });
  // The branch prices the receipt sets that are not below their floor (those are in `below` already): each needs the PIN too.
  const branchSets = lines.flatMap((l, i) => {
    const p = plans[i]!;
    return p.setsBranch && !p.below ? [{ what: l.product.displayName, kept: p.kept!, current: p.current! }] : [];
  });
  const pinNeeded = below.length > 0 || branchSets.length > 0;
  const total = sumCents(plans.map((p) => cents(p.total ?? 0)));
  const unitCount = plans.reduce((n, p) => n + (p.qty ?? 0), 0);

  const post = async () => {
    if (busy || waiting.length > 0) return;
    setBusy(true);
    setError(null);
    setCancelled(false);
    const sendKey = lost?.key ?? key;
    try {
      const prices = below.map((b) => `${b.what}: ${formatMoney(b.kept)} — below ${formatMoney(b.floor)}`);
      const sets = branchSets.map((b) => `${b.what}: sets ${possessive(acting.name)} price to ${formatMoney(b.kept)}`);
      const said = [
        ...(prices.length > 0 ? [`${prices.length === 1 ? "A price is" : `${prices.length} prices are`} kept below the floor of the discount threshold — ${prices.join("; ")}.`] : []),
        ...(sets.length > 0 ? [`${sets.length === 1 ? "A line sets" : `${sets.length} lines set`} the branch's own price, which needs an authoriser's PIN whatever its floor — ${sets.join("; ")}.`] : []),
      ];
      const r = await withStepUp((g) => goodsReceipts.post(body, { ...g, idempotencyKey: sendKey }), {
        title: "Price override at receipt", reason: "none",
        // A branch price kept at or above its floor asks no reason (UC-PURCHASING-005 A10 step 2), so none is promised for it.
        description: `${said.length > 0 ? said.join(" ") : "A price kept on this receipt is below the floor of the discount threshold, or sets a branch price."} One Control PIN authorises the receipt; each such price is written to the Control Log, with its reason where one was given.`,
      });
      if (r) { setLost(null); onPosted(r.data); }
      else setCancelled(true);
    } catch (err) {
      setError(err);
      // The answer is lost or the post still running: the key stays. So it does once the receipt is known posted, its answer not
      // kept (409 REQUEST_COMPLETED; the verification of round 3, 25 Oct 2026): another post is that answer again, never a second
      // delivery. Any other answer settles it — the receipt was refused and nothing was written, or (the key reused) it was posted as
      // first sent — and the next post is a new request.
      if ((isNetworkError(err) && !err.offline) || (isApiError(err) && err.code === "REQUEST_IN_FLIGHT") || carriedOut(err)) setLost({ key: sendKey, bodyText: lost?.bodyText ?? bodyText });
      else if (!isNetworkError(err)) setLost(null);
      takeFresh(err);
    } finally { setBusy(false); }
  };

  /** E5: a kept price that wants a reason comes back with the proposal the server makes now; it replaces the one shown. */
  const takeFresh = (err: unknown) => {
    if (!isApiError(err) || err.code !== "VALIDATION_FAILED") return;
    const meta = err.meta as { proposedCents?: unknown; currentCents?: unknown; lines?: unknown };
    const metaLines = Array.isArray(meta.lines) ? (meta.lines as { line?: unknown; proposedCents?: unknown; currentCents?: unknown }[]) : [];
    const failing = [...new Set(err.errors.map((e) => /^lines\.(\d+)\.(?:units\.\d+\.)?priceReason$/.exec(e.path)?.[1]).filter((x): x is string => !!x).map(Number))];
    if (failing.length === 0) return;
    const num = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) ? v : null);
    setLines((ls) => ls.map((l, i) => {
      if (!failing.includes(i)) return l;
      const m = metaLines.find((x) => x.line === i) ?? (i === failing[0] ? { proposedCents: meta.proposedCents, currentCents: meta.currentCents } : undefined);
      const p = plans[i];
      if (!m || !p || p.cost === null) return l;
      // What was kept stays kept: the fresh proposal never moves a price the receiver saw.
      return { ...l, keptText: l.keptText ?? textFromCents(p.kept), fresh: { costCents: p.cost, proposedCents: num(m.proposedCents), currentCents: num(m.currentCents) ?? p.current } };
    }));
  };

  // --- the refusals, under their fields ------------------------------------------------------------------------------------
  const errs = isApiError(error) && error.code === "VALIDATION_FAILED" ? error.errors : [];
  const errAt = (path: string, words?: (code: string, message: string) => string): string | undefined => {
    const f = errs.find((e) => e.path === path);
    if (!f) return undefined;
    return words ? words(f.code, f.message) : f.message;
  };
  /** Whether a refused field is on the screen to hold its message; any other is said in the banner, in words only. */
  const placed = (path: string): boolean => {
    if (["supplierId", "receivedDate", "deliveryNoteNumber", "reason", "notes", "lines"].includes(path)) return true;
    const m = /^lines\.(\d+)\.(productId|quantity|unitCostCents|keptPriceCents|priceReason|units)$/.exec(path);
    if (m) return Number(m[1]) < lines.length;
    const u = /^lines\.(\d+)\.units\.(\d+)\.(supplierSerial|keptPriceCents|priceReason)$/.exec(path);
    return !!u && Number(u[1]) < lines.length && Number(u[2]) < lines[Number(u[1])]!.units.length;
  };
  const unplaced = errs.filter((e) => !placed(e.path));
  const supplierErr = errAt("supplierId", (c, m) => (c === "inactive" ? "This supplier is inactive: choose an active one" : m));
  const dateErr = errAt("receivedDate", (c) => (c === "future" ? "Not a day still to come: the goods cannot have arrived yet" : "A date no later than today"));
  const noteErr = errAt("deliveryNoteNumber", () => "At most 64 characters");
  const reasonErr = errAt("reason", (c, m) => (c === "too_big" || c === "too_long" ? "At most 500 characters" : m));
  const notesErr = errAt("notes", () => "At most 2 000 characters");
  const linesErr = errAt("lines");

  return (
    <section className="screen" id="s-receive-stock" onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); settleCosts(); void post(); } }}>
      <div className="content">
        <PageHead title={`Receive stock — ${acting.name}`} sub={`Goods received without a purchase order · numbered GRN-${acting.code || "…"}-… when posted · stock valued at weighted average cost`} actions={back} />
        {cancelled && <Alert tone="warn"><b>Not posted.</b> The Control PIN was cancelled; the receipt is as you typed it.</Alert>}
        {error !== null && (errs.length === 0 || unplaced.length > 0) && <PostRefusal error={error} unplaced={unplaced.map((e) => e.message)} branchName={acting.name} editedSinceLost={lost !== null && lost.bodyText !== bodyText} />}
        <Panel title="Delivery" meta={`${acting.name} · no purchase order`}>
          <div className="row receive-head">
            <SupplierPicker id="receive-supplier" label="Supplier" required value={supplier} error={supplierErr}
              help={supplier ? <>{supplier.name} · {supplier.accountNumber ? <>account <span className="mono">{supplier.accountNumber}</span></> : `no account at ${acting.name}`}</> : "An active supplier of the organisation"}
              onPick={(x) => { setSupplier(x); setCancelled(false); }} />
            <Field label="Received" required htmlFor="receive-date" error={dateErr ?? (receivedDate > today ? "Not a day still to come: the goods cannot have arrived yet" : undefined)}
              help={`The day the goods arrived; new serials are numbered by it (${dayWords(receivedDate || today)})`}>
              <TextInput id="receive-date" type="date" mono max={today} value={receivedDate} error={!!dateErr} onChange={(e) => setReceivedDate(e.target.value)} />
            </Field>
            <Field label="Delivery note" htmlFor="receive-note" error={noteErr} help="The supplier's delivery note number">
              <TextInput id="receive-note" mono maxLength={PURCHASING_LIMITS.deliveryNoteMax} value={note} error={!!noteErr} onChange={(e) => setNote(e.target.value)} onBlur={() => setNoteChecked(note.trim())} />
            </Field>
          </div>
          {dupRow && (
            <Alert tone="warn"><b>Delivery note {noteChecked} is already on <ReceiptLink id={dupRow.id} number={dupRow.number} /> ({dayWords(dupRow.receivedDate)}).</b> Check the delivery was not taken in already; the receipt can still be posted.</Alert>
          )}
          <div className="grid-2">
            <Field label="Reason there is no purchase order" required htmlFor="receive-reason" error={reasonErr} help="Kept with the receipt — “Delivered by the rep on a sales call”">
              <TextArea id="receive-reason" rows={2} maxLength={PURCHASING_LIMITS.reasonMax} value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <Field label="Notes" htmlFor="receive-notes" error={notesErr}>
              <TextArea id="receive-notes" rows={2} maxLength={PURCHASING_LIMITS.receiptNotesMax} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
        </Panel>
        <Panel title="Lines" meta={`${lines.length} line${lines.length === 1 ? "" : "s"} · ${unitCount} unit${unitCount === 1 ? "" : "s"} · cost ex VAT, prices incl. VAT`} tight
          foot={<>
            <span className="small">Total <span className="mono strong">{formatMoney(total)}</span> ex VAT<span className="faint"> · a receipt calculates no VAT; the supplier&#39;s invoice becomes a bill from Sprint 7</span></span>
            <span className="right row c gap-8 post">
              {waiting.length > 0 && <span className="small faint">Waiting for {waiting.slice(0, 3).join(", ")}{waiting.length > 3 ? ` and ${waiting.length - 3} more` : ""}</span>}
              <button className="btn primary" type="button" disabled={busy || !online || waiting.length > 0} title={blockTitle ?? (waiting.length > 0 ? `Waiting for ${waiting.join(", ")}` : "Ctrl+Enter posts")} onClick={() => void post()}>
                {busy ? "Posting…" : "Post receipt"}{pinNeeded && <LockMark />}
              </button>
            </span>
          </>}>
          <div className="receive-scan">
            <Field label="Add product" htmlFor="receive-scan" error={linesErr}>
              <div className="row c gap-8">
                <div className="input w-scan">
                  <Icon name="barcode" />
                  <input id="receive-scan" ref={scanRef} autoComplete="off" placeholder="Scan a barcode or type an SKU or a name, and press Enter" maxLength={100} value={scan}
                    onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void resolve(scan); } }} />
                </div>
                {/* A tablet's camera reads a code into the same lookup (TC-PURCHASING-129; the completeness audit of 23 Oct 2026). */}
                <CameraScan testId="receive-camera" onCode={(code) => { setScan(code); void resolve(code); }} onMiss={(words) => setScanMsg(words)} />
              </div>
            </Field>
            {scanMsg && <div className="help" role="status">{scanMsg}</div>}
            {choices && (
              <div className="list bordered" role="listbox" aria-label="Matching products">
                {choices.map((p) => <div key={p.id} className="item"><div className="grow"><span className="mono">{p.sku}</span> {p.displayName}{p.supplier ? <span className="small faint"> · {p.supplier.name}</span> : null}</div><button className="btn sm" type="button" onClick={() => { addLine(p); setScan(""); }}>Add</button></div>)}
              </div>
            )}
          </div>
          {lines.length > 0 ? (
            <LinesTable lines={lines} plans={plans} branchName={acting.name} receivedDate={receivedDate || today} thresholds={thresholds} errAt={errAt}
              onChange={change} onUnit={changeUnit} onRemove={(k) => { setLines((ls) => ls.filter((l) => l.key !== k)); setCancelled(false); }} onSettle={settleCosts} toScan={toScan} onFieldScan={scanFromLine} />
          ) : <div className="empty"><b>No lines yet.</b><span>Scan each product&#39;s barcode or type its SKU; a frame gets one serial per unit.</span></div>}
          {pinNeeded && <div className="help receive-pin"><LockMark>Requires Control PIN</LockMark> — {[
            ...(below.length > 0 ? [`${below.length === 1 ? "a price is" : `${below.length} prices are`} kept below the floor of the discount threshold (${below.map((b) => `${formatMoney(b.kept)} — below ${formatMoney(b.floor)}`).join("; ")})`] : []),
            ...(branchSets.length > 0 ? [`${branchSets.length === 1 ? "a line sets" : `${branchSets.length} lines set`} ${possessive(acting.name)} own price (${branchSets.map((b) => formatMoney(b.kept)).join("; ")})`] : []),
          ].join("; and ")}. One authoriser&#39;s PIN covers the receipt; each price is written to the Control Log, with its reason where one was given.</div>}
        </Panel>
      </div>
    </section>
  );
}

/**
 * A refusal the fields do not hold, in a sentence (E8–E14); a request that may have reached the server says it can be sent again,
 * and — once the receipt is edited — what sending it again does: the same key carries it, so a receipt that did arrive stands as it
 * was sent and the edit is not posted (A12; TC-PURCHASING-114).
 */
function PostRefusal({ error, unplaced, branchName, editedSinceLost }: { error: unknown; unplaced: string[]; branchName: string; editedSinceLost: boolean }) {
  if (isNetworkError(error) && !error.offline) {
    return <Alert tone="warn"><b>The receipt may not have been posted.</b> Post it again — it will not be received twice.{editedSinceLost ? " If it reached Vision X, the receipt as first sent stands and the changes made since are not posted." : ""}</Alert>;
  }
  // Posted, its answer not kept (UC-PURCHASING-005 A12 step 2): said as posted — never "Not posted." — with where to find it.
  if (carriedOut(error)) {
    return <Alert tone="ok"><b>The receipt was posted: its answer did not arrive.</b> Find it under <Link to="/purchasing/receipts">Goods receipts</Link> — posting it again will not take the delivery in twice.</Alert>;
  }
  if (isApiError(error) && error.code === "IDEMPOTENCY_KEY_REUSED") {
    return <Alert tone="warn"><b>The receipt was posted before the answer was lost</b> — as it was then; the changes made since were not posted. Find it under <Link to="/purchasing/receipts">Goods receipts</Link>. Posting it again would take this delivery in a second time.</Alert>;
  }
  const a = isApiError(error) ? (error as ApiError) : null;
  const words = !a ? describeError(error)
    : a.code === "VALIDATION_FAILED" ? unplaced.join("; ") || "Some details need correcting."
      : a.code === "REQUEST_IN_FLIGHT" ? `This receipt is still being posted. Wait a moment, then post it again — it will not be received twice.${editedSinceLost ? " The receipt as first sent stands and the changes made since are not posted." : ""}`
        : a.code === "VERSION_CONFLICT" ? "The product's stock changed twice while the receipt was being posted; nothing was taken in. Post it again."
          : a.code === "IMMUTABLE_RECORD" && a.meta.reason === "closed" ? `${branchName} is inactive and accepts no new receipts.`
            : a.code === "NOT_FOUND" ? "The supplier or a product on the receipt no longer exists. Choose it again."
              : a.code === "STEP_UP_REQUIRED" ? "A kept price below its floor, or one that sets a branch price, needs an authoriser's Control PIN."
                : describeError(error);
  return <Alert tone="crit"><b>Not posted.</b> {words}</Alert>;
}

/** The proposal beside the current price (step 5), or what the screen offers without one (A9, A10). */
function proposalLine(l: LineDraft, p: LinePlan, branchName: string): ReactNode {
  if (p.cost === null) return <span className="faint">Enter the unit cost ex VAT for a proposal</span>;
  if (p.pending) return <span className="faint">Working out the proposal…</span>;
  const current = p.current === null ? "no current price" : `current ${formatMoney(p.current)}`;
  if (!p.proposing) return <>{possessive(branchName)} prices are not proposed at receipt · {current}{p.current !== null ? " — left blank, it sells at that" : ""}</>;
  if (l.fresh && l.fresh.costCents === p.cost) {
    return <>Proposed now <span className="mono strong">{formatMoney(p.proposed)}</span> — bands or markups changed since · {current}</>;
  }
  if (p.proposed === null) {
    const what = l.product.category === "Frame" ? `No band covers ${formatMoney(p.cost)}` : `No markup prices ${l.product.sku}`;
    return p.current === null ? <>{what} and it has no price — type its price</> : <>{what} — the current price {formatMoney(p.current)} is offered</>;
  }
  return <>Proposed <span className="mono strong">{formatMoney(p.proposed)}</span> · {p.sug ? suggestionBasis(p.sug, branchName) : ""} · {current}</>;
}

/** What a kept price will change (BR-PRICING-016), from the price in force now; posting decides it again in the receipt's order. */
function changeWords(l: LineDraft, p: LinePlan, kept: number | null, unit: boolean, branchName: string): string {
  if (kept === null) return p.current === null ? "No price kept — it has no price yet" : `No price kept — it sells at ${formatMoney(p.current)}`;
  if (p.current === null) return unit ? `This unit's ${formatMoney(kept)} becomes the product's price — it has none` : `${formatMoney(kept)} becomes the product's price — it has none`;
  if (kept === p.current) return unit || l.product.trackSerials ? "Sells at the product's price — no price changes" : "No price changes";
  if (l.product.trackSerials) return `${unit ? "this unit sells" : (p.qty ?? 0) === 1 ? "the unit sells" : "these units sell"} at ${formatMoney(kept)}; the product stays ${formatMoney(p.current)}`;
  return `sets ${possessive(branchName)} price to ${formatMoney(kept)} (now ${formatMoney(p.current)})`;
}

function LinesTable({ lines, plans, branchName, receivedDate, thresholds, errAt, onChange, onUnit, onRemove, onSettle, toScan, onFieldScan }: {
  lines: LineDraft[]; plans: LinePlan[]; branchName: string; receivedDate: string; thresholds: Thresholds;
  errAt: (path: string, words?: (code: string, message: string) => string) => string | undefined;
  onChange: (key: string, f: (l: LineDraft) => LineDraft) => void; onUnit: (key: string, uKey: string, f: (u: UnitDraft) => UnitDraft) => void;
  onRemove: (key: string) => void; onSettle: () => void; toScan: (e: KeyboardEvent<HTMLInputElement>) => void;
  onFieldScan: (lineKey: string, field: "qty" | "cost", before: string, code: string) => void;
}) {
  // Each quantity and unit cost box watches its own keys for a scanner's code (A19): Enter ending one is a scan, not the box's.
  const collectors = useRef(new Map<string, ReturnType<typeof fieldScanCollector>>());
  const figureKeys = (lineKey: string, field: "qty" | "cost") => (e: KeyboardEvent<HTMLInputElement>) => {
    const id = `${lineKey}:${field}`;
    const c = collectors.current.get(id) ?? fieldScanCollector();
    collectors.current.set(id, c);
    const hit = c.take(e.nativeEvent, e.currentTarget.value);
    if (!hit) { toScan(e); return; }
    e.preventDefault();
    onFieldScan(lineKey, field, hit.before, hit.code);
  };
  return (
    <table className="receive-lines">
      <thead><tr><th className="r">#</th><th>Product</th><th className="r">Qty</th><th className="r">Unit cost ex VAT</th><th className="r">Line total</th><th></th></tr></thead>
      {lines.map((l, i) => {
        const p = plans[i]!;
        const sku = l.product.sku;
        const at = `lines.${i}`;
        const productErr = errAt(`${at}.productId`, (c, m) => (c === "not_stocked" ? "This product holds no stock — a service, a made-to-order lens or a composite is not received" : c === "inactive" ? "This product is inactive: it is not received" : m));
        const qtyErr = errAt(`${at}.quantity`, () => `A whole number of units from 1 to ${formatCount(LINE_QUANTITY_MAX)}`);
        const costErr = errAt(`${at}.unitCostCents`, () => "A cost from R 0.00 to R 999 999.99, ex VAT");
        const keptErr = errAt(`${at}.keptPriceCents`, (c, m) => (c === "required" ? m : "A price from R 0.00 to R 999 999.99"));
        const reasonErr = errAt(`${at}.priceReason`, (c, m) => (c === "too_big" || c === "too_long" ? "At most 500 characters" : m));
        const unitsErr = errAt(`${at}.units`);
        const floor = floorOf(p.current, thresholds);
        const serial = l.product.trackSerials;
        const generated = l.units.filter((u) => !u.serial.trim()).length;
        const keptValue = l.keptText ?? textFromCents(p.offered);
        return (
          <tbody key={l.key} className="receive-line">
            <tr className="line-row">
              <td className="r faint num lineno">{i + 1}</td>
              <td className="product">
                <div className="primary">{l.product.displayName} · <span className="mono">{sku}</span></div>
                {serial && <div className="small faint">{l.units.length > 0 && generated > 0 ? `${generated} serial${generated === 1 ? "" : "s"} will be generated for ${dayWords(receivedDate)}` : "serialised: one serial per unit"}</div>}
                {(productErr || unitsErr) && <div className="error" role="alert">{productErr ?? unitsErr}</div>}
                {serial && (p.qty ?? 0) > UNITS_MAX && <div className="error" role="alert">At most {UNITS_MAX} serialised units on one receipt</div>}
              </td>
              <td className="r" data-label="Qty">
                <TextInput id={`qty-${l.key}`} aria-label={`Quantity of ${sku}`} boxClass="w-80" mono inputMode="numeric" value={l.qtyText} error={!!qtyErr || p.qty === null}
                  onChange={(e) => { const v = e.target.value; onChange(l.key, (x) => withUnits(x, v)); }} onKeyDown={figureKeys(l.key, "qty")} />
                {qtyErr && <div className="error" role="alert">{qtyErr}</div>}
              </td>
              <td className="r" data-label="Unit cost ex VAT">
                <TextInput aria-label={`Unit cost of ${sku}`} boxClass="w-120" mono inputMode="decimal" placeholder="Cost ex VAT" value={l.costText} error={!!costErr || (l.costText.trim() !== "" && p.cost === null)}
                  onChange={(e) => { const v = e.target.value; onChange(l.key, (x) => ({ ...x, costText: v })); }} onBlur={onSettle} onKeyDown={figureKeys(l.key, "cost")} />
                {costErr && <div className="error" role="alert">{costErr}</div>}
              </td>
              <td className="money" data-label="Line total">{p.total === null ? <span className="faint">—</span> : formatMoney(p.total)}</td>
              <td className="r remove"><button className="btn sm ghost" type="button" onClick={() => onRemove(l.key)}>Remove</button></td>
            </tr>
            <tr className="price-row">
              <td></td>
              <td colSpan={5}>
                <div className="price-line">
                  <div className="proposal small" data-proposal={l.key}>{proposalLine(l, p, branchName)}</div>
                  {p.cost !== null && !p.pending && (
                    <div className="row c gap-8 price-fields">
                      <Field label={serial ? "Kept, each unit (incl. VAT)" : "Kept (incl. VAT)"} htmlFor={`kept-${l.key}`} error={keptErr ?? (p.keptInvalid ? "An amount in rand, such as 2100.00" : p.needsPrice ? "Type its price: nothing proposes one" : undefined)}>
                        <div className="row c gap-8">
                          <TextInput id={`kept-${l.key}`} boxClass="w-120" mono inputMode="decimal" value={keptValue} placeholder={p.offered !== null ? textFromCents(p.offered) : "No price"} error={!!keptErr || p.keptInvalid || p.needsPrice}
                            onChange={(e) => { const v = e.target.value; onChange(l.key, (x) => ({ ...x, keptText: v })); }} />
                          {p.offered !== null && p.kept !== p.offered && <button className="btn sm" type="button" onClick={() => onChange(l.key, (x) => ({ ...x, keptText: null, reason: "" }))}>Use {formatMoney(p.offered)}</button>}
                        </div>
                      </Field>
                      {(p.needsReason || !!reasonErr) && (
                        <Field label="Reason for the price" required htmlFor={`reason-${l.key}`} error={reasonErr}>
                          <TextInput id={`reason-${l.key}`} maxLength={PURCHASING_LIMITS.reasonMax} value={l.reason} error={!!reasonErr} placeholder="Matching Centurion shelf price"
                            onChange={(e) => { const v = e.target.value; onChange(l.key, (x) => ({ ...x, reason: v })); }} />
                        </Field>
                      )}
                      <div className="small change" data-change={l.key}>
                        {changeWords(l, p, p.kept, false, branchName)}
                        {p.below && floor !== null && <> · <LockMark>below the {formatMoney(floor)} floor: Control PIN</LockMark></>}
                        {!p.below && p.setsBranch && <> · <LockMark>a branch price: Control PIN</LockMark></>}
                      </div>
                    </div>
                  )}
                </div>
              </td>
            </tr>
            {serial && l.units.length > 0 && (
              <tr className="units-row">
                <td></td>
                <td colSpan={5}>
                  <table className="units">
                    <thead><tr><th className="r">Unit</th><th>Supplier serial or barcode</th><th>Kept (incl. VAT)</th><th>Reason · what it changes</th></tr></thead>
                    <tbody>
                      {l.units.map((u, j) => {
                        const up = p.units[j]!;
                        const uAt = `${at}.units.${j}`;
                        const serialErr = errAt(`${uAt}.supplierSerial`, (c, m) => (c === "too_big" || c === "too_long" ? "At most 64 characters" : m)) ?? up.serialIssue ?? undefined;
                        const uKeptErr = errAt(`${uAt}.keptPriceCents`, () => "A price from R 0.00 to R 999 999.99");
                        const uReasonErr = errAt(`${uAt}.priceReason`, (c, m) => (c === "too_big" || c === "too_long" ? "At most 500 characters" : m));
                        return (
                          <tr key={u.key} data-unit={`${i}.${j}`}>
                            <td className="r num faint unitno" data-label="Unit">{j + 1}</td>
                            <td data-label="Supplier serial or barcode">
                              {/* Enter moves to this line's next unit, and from its last back to the scan box, so a scanner's next code adds
                                  a unit and never runs into this serial; by the unit's own key, so a product on two lines (A15) keeps to
                                  its own (the review of 18 Oct 2026, D1-3; TC-PURCHASING-113). */}
                              <TextInput id={`serial-${u.key}`} aria-label={`Supplier serial of unit ${j + 1} of ${sku}`} boxClass="w-serial" mono maxLength={64} value={u.serial} placeholder="generated" error={!!serialErr}
                                onChange={(e) => { const v = e.target.value; onUnit(l.key, u.key, (x) => ({ ...x, serial: v })); }}
                                onKeyDown={(e) => {
                                  const next = l.units[j + 1];
                                  if (e.key !== "Enter" || e.ctrlKey || e.metaKey) return;
                                  if (!next) { toScan(e); return; }
                                  e.preventDefault();
                                  document.getElementById(`serial-${next.key}`)?.focus();
                                }} />
                              {serialErr ? <div className="error" role="alert">{serialErr}</div> : !u.serial.trim() && <div className="help">generated ({dayWords(receivedDate)})</div>}
                            </td>
                            <td data-label="Kept (incl. VAT)">
                              {p.cost !== null && !p.pending ? (
                                <div className="row c gap-8">
                                  <TextInput aria-label={`Kept price of unit ${j + 1} of ${sku}`} boxClass="w-120" mono inputMode="decimal" value={u.keptText ?? textFromCents(p.kept)} placeholder={textFromCents(p.kept) || "No price"} error={!!uKeptErr || up.invalid}
                                    onChange={(e) => { const v = e.target.value; onUnit(l.key, u.key, (x) => ({ ...x, keptText: v })); }} />
                                  {up.own && <button className="btn sm ghost" type="button" title="The line's price" onClick={() => onUnit(l.key, u.key, (x) => ({ ...x, keptText: null, reason: "" }))}>{p.kept === null ? "Clear" : `Use ${formatMoney(p.kept)}`}</button>}
                                </div>
                              ) : <span className="faint">—</span>}
                              {(uKeptErr || up.invalid) && <div className="error" role="alert">{uKeptErr ?? "An amount in rand, such as 1990.00"}</div>}
                            </td>
                            <td className="unit-reason">
                              {(up.needsReason || !!uReasonErr) && (
                                <Field label={`Reason for unit ${j + 1}'s price`} required htmlFor={`reason-${u.key}`} error={uReasonErr}>
                                  <TextInput id={`reason-${u.key}`} maxLength={PURCHASING_LIMITS.reasonMax} value={u.reason} error={!!uReasonErr}
                                    onChange={(e) => { const v = e.target.value; onUnit(l.key, u.key, (x) => ({ ...x, reason: v })); }} />
                                </Field>
                              )}
                              {up.own && <div className="small">{changeWords(l, p, up.kept, true, branchName)}{up.below && floor !== null && <> · <LockMark>below the {formatMoney(floor)} floor: Control PIN</LockMark></>}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </td>
              </tr>
            )}
          </tbody>
        );
      })}
    </table>
  );
}
